// PakMazar Admin translations.
//
// Three languages, one key set. `en` is the reference: `t()` falls back to it
// so a key that is missing from a translation renders readable English rather
// than a raw key like `complaints.approveRefund`.
//
// Translate LABELS only. Never translate stored data — customer names, admin
// notes, emails, ids and proper cemetery names are shown exactly as entered.

export const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'az', label: 'Azərbaycan', short: 'AZ' },
  { code: 'ru', label: 'Русский', short: 'RU' },
];

export const translations = {
  en: {
    // Nav
    dashboard: 'Dashboard', orders: 'Orders', workReviews: 'Work Reviews', workers: 'Workers', clients: 'Clients',
    cemeteries: 'Cities & Cemeteries', packages: 'Packages', flowers: 'Flowers', promoCodes: 'Promo Codes', users: 'New Users', newUsers: 'New Users',
    communications: 'Messages', support: 'Support', finance: 'Finance',
    adminAccess: 'Admin Access', logout: 'Log out',
    adminPanel: 'Admin Panel',
    // Dashboard
    totalOrders: 'Total Orders', completed: 'Completed', pending: 'Pending',
    revenue: 'Total Revenue', workersCount: 'Workers', customers: 'Customers',
    recentOrders: 'Recent Orders', monthlyOrders: 'Monthly Orders',
    statusBreakdown: 'Status Breakdown', workerActivity: 'Worker Activity',
    ordersMap: 'Order Locations', refresh: 'Refresh', loading: 'Loading...',
    noData: 'No data', inProgress: 'In Progress', cancelled: 'Cancelled',
    // Orders
    orderNo: 'Order #', deceased: 'Deceased', cemetery: 'Cemetery',
    greenArea: 'Green area', greenYes: 'Yes', greenNo: 'No',
    amount: 'Amount', status: 'Status', date: 'Date', details: 'Details',
    all: 'All', filterBy: 'Filter by status', changeStatus: 'Change Status',
    editLocation: 'Edit Location', saveLocation: 'Save Location',
    cemeteryName: 'Cemetery Name', graveDesc: 'Grave Description',
    latitude: 'Latitude (optional)', longitude: 'Longitude (optional)',
    cancel: 'Cancel', save: 'Save', worker: 'Worker', customer: 'Customer',
    package: 'Package', payment: 'Payment', scheduledDate: 'Scheduled Date',
    graveCoords: 'Coordinates', photos: 'Photos', trackingEvents: 'Tracking',
    locationEdit: '📍 Edit Location', close: 'Close', assign: 'Assign Worker',
    // Workers
    addWorker: '+ Add Worker', name: 'Name', phone: 'Phone', rating: 'Rating',
    block: 'Block', activate: 'Activate', blocked: 'Blocked', active: 'Active',
    email: 'Email', password: 'Password', fullName: 'Full Name',
    workerAdded: 'Worker added successfully!', error: 'Error',
    totalCompleted: 'Completed Jobs', activeJob: 'Active Job', noActiveJob: 'No active job',
    assignedOrders: 'Assigned Orders',
    // Cemeteries
    addCity: '+ City', addCemetery: '+ Cemetery', cities: 'Cities',
    cemName: 'Cemetery Name', cemNameAz: 'Name (AZ)', cemNameRu: 'Name (RU)',
    city: 'City', coords: 'Coordinates', activeStatus: 'Active', inactiveStatus: 'Inactive',
    deactivate: 'Deactivate', makeActive: 'Activate', importCSV: 'Import CSV',
    csvFormat: 'CSV format: name,cityId,lat,lng,imageUrl',
    uploadPhoto: 'Upload Photo', photoUrl: 'Photo URL', orPaste: 'or paste URL:',
    cityAdded: 'City added', cemAdded: 'Cemetery added', importDone: 'Import complete',
    selectCity: 'Select city', allCities: 'All Azerbaijan Cities',
    editCem: 'Edit Cemetery', newCity: 'New City', newCemetery: 'New Cemetery',
    // Users
    role: 'Role', joinDate: 'Joined', language: 'Language',
    // Common
    search: 'Search...', actions: 'Actions', total: 'Total',
    refreshing: 'Refreshing', review: 'Review', reason: 'Reason',
    nothingInQueue: 'Nothing in this queue.',
    // Stop service
    stopService: 'Stop Service',
    stopServiceTitle: 'Stop this service?',
    stopServiceBody:
      'The worker loses the job immediately and both the worker and the ' +
      'customer are shown your reason. The order is kept in history and ' +
      'nothing is refunded by this action.',
    stopServiceReasonHint:
      'Reason shown to the worker and the customer.',
    serviceStopped: 'Service stopped',
    serviceStoppedByTeam: 'PakMazar Team has stopped this service.',
    // Complaints
    complaints: 'Complaints',
    complaintsDescription:
      'Customer complaints about completed or stopped services, and the ' +
      'refund decision that can follow one.',
    complaint: 'Complaint',
    complaintSubmitted: 'Complaint submitted',
    underReview: 'Under Review',
    refundApproved: 'Refund approved',
    refundRejected: 'Refund rejected',
    resolvedWithoutRefund: 'Resolved without refund',
    startReview: 'Start Review',
    approveRefund: 'Approve Refund',
    rejectRefund: 'Reject Refund',
    resolveWithoutRefund: 'Resolve without Refund',
    decisionNote: 'Decision note',
    decisionNoteHint:
      'The customer is shown this text exactly as written.',
    refundAmount: 'Refund amount',
    refundableNow: 'Refundable now',
    customerComplaint: 'Customer complaint',
    customerEvidence: 'Customer evidence',
    adminDecision: 'Admin decision',
    caseHistory: 'Case history',
  },

  az: {
    // Nav
    dashboard: 'İdarə Paneli', orders: 'Sifarişlər', workReviews: 'İşlərin yoxlanılması', workers: 'İşçilər', clients: 'Müştərilər',
    cemeteries: 'Şəhərlər və Qəbiristanlıqlar', packages: 'Paketlər', flowers: 'Güllər', promoCodes: 'Promo Kodları', users: 'Yeni İstifadəçilər', newUsers: 'Yeni İstifadəçilər',
    communications: 'Mesajlar', support: 'Dəstək', finance: 'Maliyyə',
    adminAccess: 'Admin Girişi', logout: 'Çıxış',
    adminPanel: 'Admin Panel',
    // Dashboard
    totalOrders: 'Ümumi Sifarişlər', completed: 'Tamamlandı', pending: 'Gözlənilir',
    revenue: 'Ümumi Gəlir', workersCount: 'İşçilər', customers: 'Müştərilər',
    recentOrders: 'Son Sifarişlər', monthlyOrders: 'Aylıq Sifarişlər',
    statusBreakdown: 'Status Bölgüsü', workerActivity: 'İşçi Aktivliyi',
    ordersMap: 'Sifariş Yerləri', refresh: 'Yenilə', loading: 'Yüklənir...',
    noData: 'Məlumat yoxdur', inProgress: 'İcrada', cancelled: 'Ləğv edildi',
    // Orders
    orderNo: 'Sifariş #', deceased: 'Mərhum', cemetery: 'Qəbiristanlıq',
    greenArea: 'Yaşıllıq sahəsi', greenYes: 'Var', greenNo: 'Yoxdur',
    amount: 'Məbləğ', status: 'Status', date: 'Tarix', details: 'Detallar',
    all: 'Hamısı', filterBy: 'Statusa görə', changeStatus: 'Statusu Dəyiş',
    editLocation: 'Məkanı Dəyiş', saveLocation: 'Məkanı Saxla',
    cemeteryName: 'Qəbiristanlıq Adı', graveDesc: 'Məzar Təsviri',
    latitude: 'Enlik (könüllü)', longitude: 'Uzunluq (könüllü)',
    cancel: 'Ləğv et', save: 'Saxla', worker: 'İşçi', customer: 'Müştəri',
    package: 'Paket', payment: 'Ödəniş', scheduledDate: 'Tarix',
    graveCoords: 'Koordinatlar', photos: 'Fotolar', trackingEvents: 'İzləmə',
    locationEdit: '📍 Məkanı Dəyiş', close: 'Bağla', assign: 'İşçi Təyin Et',
    // Workers
    addWorker: '+ İşçi Əlavə Et', name: 'Ad', phone: 'Telefon', rating: 'Reytinq',
    block: 'Blokla', activate: 'Aktivləşdir', blocked: 'Bloklandı', active: 'Aktiv',
    email: 'E-poçt', password: 'Şifrə', fullName: 'Ad Soyad',
    workerAdded: 'İşçi uğurla əlavə edildi!', error: 'Xəta',
    totalCompleted: 'Tamamlanan İşlər', activeJob: 'Aktiv İş', noActiveJob: 'Aktiv iş yoxdur',
    assignedOrders: 'Təyin Olunmuş Sifarişlər',
    // Cemeteries
    addCity: '+ Şəhər', addCemetery: '+ Qəbiristanlıq', cities: 'Şəhərlər',
    cemName: 'Qəbiristanlıq Adı', cemNameAz: 'Ad (AZ)', cemNameRu: 'Ad (RU)',
    city: 'Şəhər', coords: 'Koordinatlar', activeStatus: 'Aktiv', inactiveStatus: 'Deaktiv',
    deactivate: 'Deaktiv et', makeActive: 'Aktivləşdir', importCSV: 'CSV İdxal',
    csvFormat: 'CSV formatı: name,cityId,lat,lng,imageUrl',
    uploadPhoto: 'Foto Yüklə', photoUrl: 'Foto URL', orPaste: 'və ya URL yapışdırın:',
    cityAdded: 'Şəhər əlavə edildi', cemAdded: 'Qəbiristanlıq əlavə edildi', importDone: 'İdxal tamamlandı',
    selectCity: 'Şəhər seçin', allCities: 'Azərbaycanın Bütün Şəhərləri',
    editCem: 'Qəbiristanlığı Redaktə Et', newCity: 'Yeni Şəhər', newCemetery: 'Yeni Qəbiristanlıq',
    // Users
    role: 'Rol', joinDate: 'Qoşulma tarixi', language: 'Dil',
    // Common
    search: 'Axtar...', actions: 'Əməliyyatlar', total: 'Cəmi',
    refreshing: 'Yenilənir', review: 'Yoxla', reason: 'Səbəb',
    nothingInQueue: 'Bu siyahıda heç nə yoxdur.',
    // Stop service
    stopService: 'Xidməti dayandır',
    stopServiceTitle: 'Bu xidmət dayandırılsın?',
    stopServiceBody:
      'İşçi dərhal işi itirir və həm işçiyə, həm də müştəriyə sizin ' +
      'səbəbiniz göstərilir. Sifariş tarixçədə qalır və bu əməliyyat ' +
      'heç bir geri ödəniş etmir.',
    stopServiceReasonHint:
      'İşçiyə və müştəriyə göstəriləcək səbəb.',
    serviceStopped: 'Xidmət dayandırılıb',
    serviceStoppedByTeam:
      'Bu xidmət PakMazar komandası tərəfindən dayandırılıb.',
    // Complaints
    complaints: 'Şikayətlər',
    complaintsDescription:
      'Tamamlanmış və ya dayandırılmış xidmətlərlə bağlı müştəri ' +
      'şikayətləri və onların nəticəsində verilə bilən geri ödəniş qərarı.',
    complaint: 'Şikayət',
    complaintSubmitted: 'Şikayət göndərildi',
    underReview: 'Baxılır',
    refundApproved: 'Geri ödəniş təsdiqləndi',
    refundRejected: 'Geri ödəniş rədd edildi',
    resolvedWithoutRefund: 'Geri ödənişsiz həll edildi',
    startReview: 'Yoxlamaya başla',
    approveRefund: 'Geri ödənişi təsdiqlə',
    rejectRefund: 'Geri ödənişi rədd et',
    resolveWithoutRefund: 'Geri ödənişsiz həll et',
    decisionNote: 'Qərar qeydi',
    decisionNoteHint:
      'Bu mətn müştəriyə eynilə göstərilir.',
    refundAmount: 'Geri ödəniş məbləği',
    refundableNow: 'Hazırda geri qaytarıla bilən',
    customerComplaint: 'Müştəri şikayəti',
    customerEvidence: 'Müştərinin sübutları',
    adminDecision: 'Admin qərarı',
    caseHistory: 'Müraciət tarixçəsi',
  },

  ru: {
    // Nav
    dashboard: 'Панель управления', orders: 'Заказы', workReviews: 'Проверка работ', workers: 'Работники', clients: 'Клиенты',
    cemeteries: 'Города и кладбища', packages: 'Пакеты', flowers: 'Цветы', promoCodes: 'Промокоды', users: 'Новые пользователи', newUsers: 'Новые пользователи',
    communications: 'Сообщения', support: 'Поддержка', finance: 'Финансы',
    adminAccess: 'Доступ администраторов', logout: 'Выйти',
    adminPanel: 'Панель администратора',
    // Dashboard
    totalOrders: 'Всего заказов', completed: 'Выполнено', pending: 'В ожидании',
    revenue: 'Общий доход', workersCount: 'Работники', customers: 'Клиенты',
    recentOrders: 'Последние заказы', monthlyOrders: 'Заказы по месяцам',
    statusBreakdown: 'Распределение по статусам', workerActivity: 'Активность работников',
    ordersMap: 'География заказов', refresh: 'Обновить', loading: 'Загрузка...',
    noData: 'Нет данных', inProgress: 'В работе', cancelled: 'Отменён',
    // Orders
    orderNo: 'Заказ №', deceased: 'Усопший', cemetery: 'Кладбище',
    greenArea: 'Зелёная зона', greenYes: 'Есть', greenNo: 'Нет',
    amount: 'Сумма', status: 'Статус', date: 'Дата', details: 'Подробности',
    all: 'Все', filterBy: 'Фильтр по статусу', changeStatus: 'Изменить статус',
    editLocation: 'Изменить местоположение', saveLocation: 'Сохранить местоположение',
    cemeteryName: 'Название кладбища', graveDesc: 'Описание могилы',
    latitude: 'Широта (необязательно)', longitude: 'Долгота (необязательно)',
    cancel: 'Отмена', save: 'Сохранить', worker: 'Работник', customer: 'Клиент',
    package: 'Пакет', payment: 'Оплата', scheduledDate: 'Дата услуги',
    graveCoords: 'Координаты', photos: 'Фото', trackingEvents: 'Отслеживание',
    locationEdit: '📍 Изменить местоположение', close: 'Закрыть', assign: 'Назначить работника',
    // Workers
    addWorker: '+ Добавить работника', name: 'Имя', phone: 'Телефон', rating: 'Рейтинг',
    block: 'Заблокировать', activate: 'Активировать', blocked: 'Заблокирован', active: 'Активен',
    email: 'Эл. почта', password: 'Пароль', fullName: 'Имя и фамилия',
    workerAdded: 'Работник успешно добавлен!', error: 'Ошибка',
    totalCompleted: 'Выполненные работы', activeJob: 'Активная работа', noActiveJob: 'Нет активных работ',
    assignedOrders: 'Назначенные заказы',
    // Cemeteries
    addCity: '+ Город', addCemetery: '+ Кладбище', cities: 'Города',
    cemName: 'Название кладбища', cemNameAz: 'Название (AZ)', cemNameRu: 'Название (RU)',
    city: 'Город', coords: 'Координаты', activeStatus: 'Активно', inactiveStatus: 'Неактивно',
    deactivate: 'Деактивировать', makeActive: 'Активировать', importCSV: 'Импорт CSV',
    csvFormat: 'Формат CSV: name,cityId,lat,lng,imageUrl',
    uploadPhoto: 'Загрузить фото', photoUrl: 'URL фото', orPaste: 'или вставьте URL:',
    cityAdded: 'Город добавлен', cemAdded: 'Кладбище добавлено', importDone: 'Импорт завершён',
    selectCity: 'Выберите город', allCities: 'Все города Азербайджана',
    editCem: 'Редактировать кладбище', newCity: 'Новый город', newCemetery: 'Новое кладбище',
    // Users
    role: 'Роль', joinDate: 'Дата регистрации', language: 'Язык',
    // Common
    search: 'Поиск...', actions: 'Действия', total: 'Всего',
    refreshing: 'Обновление', review: 'Проверить', reason: 'Причина',
    nothingInQueue: 'В этой очереди пусто.',
    // Stop service
    stopService: 'Остановить услугу',
    stopServiceTitle: 'Остановить эту услугу?',
    stopServiceBody:
      'Работник сразу потеряет доступ к заказу, а работнику и клиенту ' +
      'будет показана указанная вами причина. Заказ сохраняется в истории, ' +
      'и это действие не возвращает деньги.',
    stopServiceReasonHint:
      'Причина, которую увидят работник и клиент.',
    serviceStopped: 'Услуга остановлена',
    serviceStoppedByTeam: 'Услуга остановлена командой PakMazar.',
    // Complaints
    complaints: 'Жалобы',
    complaintsDescription:
      'Жалобы клиентов на выполненные или остановленные услуги и решение ' +
      'о возврате средств, которое может быть принято по ним.',
    complaint: 'Жалоба',
    complaintSubmitted: 'Жалоба отправлена',
    underReview: 'На рассмотрении',
    refundApproved: 'Возврат средств одобрен',
    refundRejected: 'Возврат средств отклонён',
    resolvedWithoutRefund: 'Решено без возврата средств',
    startReview: 'Начать рассмотрение',
    approveRefund: 'Одобрить возврат',
    rejectRefund: 'Отклонить возврат',
    resolveWithoutRefund: 'Решить без возврата',
    decisionNote: 'Комментарий к решению',
    decisionNoteHint:
      'Клиент увидит этот текст в точности так, как он написан.',
    refundAmount: 'Сумма возврата',
    refundableNow: 'Доступно к возврату',
    customerComplaint: 'Жалоба клиента',
    customerEvidence: 'Доказательства клиента',
    adminDecision: 'Решение администратора',
    caseHistory: 'История обращения',
  },
};

export function t(lang, key) {
  return translations[lang]?.[key] || translations.en[key] || key;
}

const STATUS_TRANSLATIONS = {
  en: {
    active: 'Active', inactive: 'Inactive', pending: 'Pending', approved: 'Approved',
    rejected: 'Rejected', disabled: 'Disabled', blocked: 'Blocked', completed: 'Completed',
    accepted: 'Accepted', arrived: 'Arrived', in_progress: 'In Progress',
    client_report_sent: 'Customer Report Sent', admin_proof_pending: 'Collecting Admin Proof',
    pending_admin_review: 'Pending Admin Review',
    changes_requested: 'Changes Requested', admin_cancelled: 'Stopped by PakMazar',
    submitted: 'Submitted', under_review: 'Under review', refund_approved: 'Refund approved',
    refunded: 'Refunded', refund_rejected: 'Refund rejected',
    resolved_without_refund: 'Resolved without refund', paid: 'Paid', unpaid: 'Unpaid',
    pending_payment: 'Pending payment', awaiting_quote: 'Awaiting quote', cancelled: 'Cancelled',
    assigned: 'Assigned', confirmed: 'Confirmed', worker_on_route: 'Worker on route',
    before_photos_uploaded: 'Before photos uploaded', after_photos_uploaded: 'After photos uploaded',
    open: 'Open', closed: 'Closed', waiting_admin: 'Waiting for Admin', waiting_customer: 'Waiting for customer',
  },
  az: {
    active: 'Aktiv', inactive: 'Qeyri-aktiv', pending: 'Gözləyir', approved: 'Təsdiqlənib',
    rejected: 'Rədd edilib', disabled: 'Deaktiv edilib', blocked: 'Bloklanıb', completed: 'Tamamlanıb',
    accepted: 'Qəbul edilib', arrived: 'Ünvana çatıb', in_progress: 'İcra olunur',
    client_report_sent: 'Müştəri hesabatı göndərilib', admin_proof_pending: 'Admin sübutları toplanır', pending_admin_review: 'Admin yoxlamasını gözləyir',
    changes_requested: 'Dəyişiklik tələb olunub', admin_cancelled: 'PakMazar tərəfindən dayandırılıb',
    submitted: 'Göndərilib', under_review: 'Araşdırılır', refund_approved: 'Geri ödəniş təsdiqlənib',
    refunded: 'Geri ödənilib', refund_rejected: 'Geri ödəniş rədd edilib',
    resolved_without_refund: 'Geri ödənişsiz həll edilib', paid: 'Ödənilib', unpaid: 'Ödənilməyib',
    pending_payment: 'Ödəniş gözlənilir', awaiting_quote: 'Qiymət təklifi gözlənilir', cancelled: 'Ləğv edilib',
    assigned: 'Təyin edilib', confirmed: 'Təsdiqlənib', worker_on_route: 'İşçi yoldadır',
    before_photos_uploaded: 'Əvvəlki fotolar yüklənib', after_photos_uploaded: 'Sonrakı fotolar yüklənib',
    open: 'Açıq', closed: 'Bağlı', waiting_admin: 'Admin cavabı gözlənilir', waiting_customer: 'Müştəri cavabı gözlənilir',
  },
  ru: {
    active: 'Активный', inactive: 'Неактивный', pending: 'Ожидает', approved: 'Одобрен',
    rejected: 'Отклонён', disabled: 'Отключён', blocked: 'Заблокирован', completed: 'Завершён',
    accepted: 'Принят', arrived: 'Исполнитель прибыл', in_progress: 'Выполняется',
    client_report_sent: 'Отчёт клиенту отправлен', admin_proof_pending: 'Сбор доказательств для администратора', pending_admin_review: 'Ожидает проверки администратора',
    changes_requested: 'Запрошены изменения', admin_cancelled: 'Остановлен командой PakMazar',
    submitted: 'Отправлен', under_review: 'На рассмотрении', refund_approved: 'Возврат средств одобрен',
    refunded: 'Средства возвращены', refund_rejected: 'Возврат средств отклонён',
    resolved_without_refund: 'Решено без возврата средств', paid: 'Оплачено', unpaid: 'Не оплачено',
    pending_payment: 'Ожидает оплаты', awaiting_quote: 'Ожидает расчёта цены', cancelled: 'Отменён',
    assigned: 'Назначен', confirmed: 'Подтверждён', worker_on_route: 'Исполнитель в пути',
    before_photos_uploaded: 'Фото до работы загружены', after_photos_uploaded: 'Фото после работы загружены',
    open: 'Открыто', closed: 'Закрыто', waiting_admin: 'Ожидает администратора', waiting_customer: 'Ожидает клиента',
  },
};

/** Translate Firestore enum values without changing user-entered text. */
export function statusText(lang, value) {
  if (value == null || value === '') return '—';
  const key = String(value).trim().toLowerCase().replace(/[\s-]+/g, '_');
  return STATUS_TRANSLATIONS[lang]?.[key] || STATUS_TRANSLATIONS.en[key] || String(value);
}
